import Papa from 'papaparse';
import {
    CompanySizeSeniorityRow,
    PositionExperienceRow,
    SalaryData,
    SalarySummaryRow,
    Seniority,
} from '../types';
import { DATA_URLS, SENIORITY_ORDER } from './constants';

type CsvRow = Record<string, string>;

const normalizeHeader = (value: string) =>
    value.trim().toLocaleLowerCase('en-US').replace(/\s+/g, ' ');

const normalizePosition = (value: string) =>
    value.trim().replace(/\s+/g, ' ');

const resolveHeader = (
    headers: string[],
    expectedHeader: string,
    fileName: string
) => {
    const normalizedExpected = normalizeHeader(expectedHeader);
    const match = headers.find(
        (header) => normalizeHeader(header) === normalizedExpected
    );

    if (!match) {
        throw new Error(
            `${fileName} dosyasında "${expectedHeader}" sütunu bulunamadı.`
        );
    }

    return match;
};

const readNumber = (
    row: CsvRow,
    header: string,
    fileName: string,
    rowIndex: number
) => {
    const value = Number(row[header]);

    if (!Number.isFinite(value)) {
        throw new Error(
            `${fileName} dosyasının ${rowIndex + 2}. satırında geçersiz sayı var.`
        );
    }

    return value;
};

const parseCsv = async (url: string, signal: AbortSignal) => {
    const response = await fetch(url, {
        signal,
        cache: 'force-cache',
    });

    if (!response.ok) {
        throw new Error(`Veri dosyası yüklenemedi: ${response.status}`);
    }

    const csvText = await response.text();
    const result = Papa.parse<CsvRow>(csvText, {
        header: true,
        skipEmptyLines: 'greedy',
    });

    const blockingErrors = result.errors.filter(
        (error) => error.code !== 'TooFewFields'
    );

    if (blockingErrors.length > 0) {
        throw new Error(
            `CSV ayrıştırma hatası: ${blockingErrors[0].message}`
        );
    }

    return {
        rows: result.data,
        headers: result.meta.fields ?? [],
    };
};

const keepLargestSample = <T extends { sampleCount: number }>(
    rows: T[],
    keyForRow: (row: T) => string
) => {
    const result = new Map<string, T>();

    rows.forEach((row) => {
        const key = keyForRow(row);
        const existing = result.get(key);

        if (!existing || row.sampleCount > existing.sampleCount) {
            result.set(key, row);
        }
    });

    return Array.from(result.values());
};

const loadSalarySummary = async (signal: AbortSignal) => {
    const fileName = 'salary_summary.csv';
    const { rows, headers } = await parseCsv(
        DATA_URLS.salarySummary,
        signal
    );
    const year = resolveHeader(headers, 'Year', fileName);
    const position = resolveHeader(headers, 'Position', fileName);
    const medianSalary = resolveHeader(
        headers,
        'Median Salary',
        fileName
    );
    const sampleCount = resolveHeader(headers, 'Sample Count', fileName);
    const averageExperience = resolveHeader(
        headers,
        'Average Experience',
        fileName
    );

    const parsed = rows.map<SalarySummaryRow>((row, index) => ({
        year: readNumber(row, year, fileName, index),
        position: normalizePosition(row[position]),
        medianSalary: readNumber(row, medianSalary, fileName, index),
        sampleCount: readNumber(row, sampleCount, fileName, index),
        averageExperience: readNumber(
            row,
            averageExperience,
            fileName,
            index
        ),
    }));

    return keepLargestSample(
        parsed,
        (row) => `${row.year}::${row.position}`
    );
};

const loadPositionExperience = async (signal: AbortSignal) => {
    const fileName = 'position_experience_salary_summary.csv';
    const { rows, headers } = await parseCsv(
        DATA_URLS.positionExperience,
        signal
    );
    const year = resolveHeader(headers, 'Year', fileName);
    const position = resolveHeader(headers, 'Position', fileName);
    const experience = resolveHeader(headers, 'Experience', fileName);
    const medianSalary = resolveHeader(
        headers,
        'Median Salary',
        fileName
    );
    const sampleCount = resolveHeader(headers, 'Sample Count', fileName);

    const parsed = rows.map<PositionExperienceRow>((row, index) => ({
        year: readNumber(row, year, fileName, index),
        position: normalizePosition(row[position]),
        experience: row[experience].trim(),
        medianSalary: readNumber(row, medianSalary, fileName, index),
        sampleCount: readNumber(row, sampleCount, fileName, index),
    }));

    return keepLargestSample(
        parsed,
        (row) => `${row.year}::${row.position}::${row.experience}`
    );
};

const loadCompanySizeSeniority = async (signal: AbortSignal) => {
    const fileName = 'company_size_seniority_salary_summary.csv';
    const { rows, headers } = await parseCsv(
        DATA_URLS.companySizeSeniority,
        signal
    );
    const year = resolveHeader(headers, 'Year', fileName);
    const companySize = resolveHeader(headers, 'Company Size', fileName);
    const seniority = resolveHeader(headers, 'Seniority', fileName);
    const medianSalary = resolveHeader(
        headers,
        'Median Salary',
        fileName
    );
    const sampleCount = resolveHeader(headers, 'Sample Count', fileName);

    return rows.map<CompanySizeSeniorityRow>((row, index) => {
        const seniorityValue = row[seniority].trim() as Seniority;

        if (!SENIORITY_ORDER.includes(seniorityValue)) {
            throw new Error(
                `${fileName} dosyasında bilinmeyen kıdem seviyesi var.`
            );
        }

        return {
            year: readNumber(row, year, fileName, index),
            companySize: row[companySize].trim(),
            seniority: seniorityValue,
            medianSalary: readNumber(
                row,
                medianSalary,
                fileName,
                index
            ),
            sampleCount: readNumber(row, sampleCount, fileName, index),
        };
    });
};

export const loadSalaryData = async (
    signal: AbortSignal
): Promise<SalaryData> => {
    const [salarySummary, positionExperience, companySizeSeniority] =
        await Promise.all([
            loadSalarySummary(signal),
            loadPositionExperience(signal),
            loadCompanySizeSeniority(signal),
        ]);

    const years = Array.from(
        new Set([
            ...salarySummary.map((row) => row.year),
            ...positionExperience.map((row) => row.year),
            ...companySizeSeniority.map((row) => row.year),
        ])
    ).sort((a, b) => b - a);

    if (years.length === 0) {
        throw new Error('Gösterilecek maaş verisi bulunamadı.');
    }

    return {
        salarySummary,
        positionExperience,
        companySizeSeniority,
        years,
    };
};
